(() => {
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const decodeValue = (value) => {
    if (typeof value !== 'string') return value;
    const textarea = document.createElement('textarea');
    textarea.innerHTML = value;
    return textarea.value;
  };
  const normalizeProperties = (properties) => {
    if (!properties) return {};
    const normalizeEntry = (result, key, value) => {
      result[decodeValue(key)] = decodeValue(value);
      return result;
    };

    if (!Array.isArray(properties) && typeof properties === 'object') {
      return Object.entries(properties).reduce((result, [key, value]) => {
        if (Array.isArray(value) && value.length >= 2) return normalizeEntry(result, value[0], value[1]);
        if (value && typeof value === 'object' && value.first) return normalizeEntry(result, value.first, value.last);
        return normalizeEntry(result, key, value);
      }, {});
    }

    if (Array.isArray(properties)) {
      return properties.reduce((result, property) => {
        if (Array.isArray(property) && property.length >= 2) normalizeEntry(result, property[0], property[1]);
        if (property && typeof property === 'object' && property.first) normalizeEntry(result, property.first, property.last);
        return result;
      }, {});
    }

    return {};
  };

  const initQrPersonalizer = (root) => {
    if (root.dataset.renaoQrReady === 'true') return;
    root.dataset.renaoQrReady = 'true';

    const preview = root.querySelector('[data-renao-qr-preview]');
    const poster = root.querySelector('[data-renao-qr-poster]');
    const photoInput = root.querySelector('[data-renao-qr-photo-input]');
    const photoButton = root.querySelector('[data-renao-qr-photo-button]');
    const photoAdjustToggle = root.querySelector('[data-renao-qr-photo-adjust-toggle]');
    const photoPreview = root.querySelector('[data-renao-qr-photo-preview]');
    const photoTitle = root.querySelector('[data-renao-qr-photo-title]');
    const photoCaption = root.querySelector('[data-renao-qr-photo-caption]');
    const audioInput = root.querySelector('[data-renao-qr-audio-input]');
    const audioButton = root.querySelector('[data-renao-qr-audio-button]');
    const audioTitle = root.querySelector('[data-renao-qr-audio-title]');
    const audioCaption = root.querySelector('[data-renao-qr-audio-caption]');
    const positionInput = root.querySelector('[data-renao-qr-photo-position]');
    const cropModal = root.querySelector('[data-renao-qr-crop]');
    const cropStage = root.querySelector('[data-renao-qr-crop-stage]');
    const cropImage = root.querySelector('[data-renao-qr-crop-image]');
    const cropZoom = root.querySelector('[data-renao-qr-crop-zoom]');
    const cropApply = root.querySelector('[data-renao-qr-crop-apply]');
    const cropCancelButtons = root.querySelectorAll('[data-renao-qr-crop-cancel]');
    const cropMoveButtons = root.querySelectorAll('[data-renao-qr-crop-move]');
    const cropZoomStepButtons = root.querySelectorAll('[data-renao-qr-crop-zoom-step]');
    const cropReset = root.querySelector('[data-renao-qr-crop-reset]');
    const noteInput = root.querySelector('[data-renao-qr-note-input]');
    const notePreview = root.querySelector('[data-renao-qr-note-preview]');
    const noteCount = root.querySelector('[data-renao-qr-note-count]');
    const fontProperty = root.querySelector('[data-renao-qr-font-property]');
    const codeProperty = root.querySelector('[data-renao-qr-code-property]');
    const orderNoteInput = root.closest('form')?.querySelector('[data-renao-qr-order-note-input]');
    const waveProperty = root.querySelector('[data-renao-qr-wave-property]');
    const wavePdf = root.querySelector('[data-renao-qr-wave-pdf]');
    const waveField = root.querySelector('.renao-qr-field--wave');
    const waveToggle = root.querySelector('[data-renao-qr-wave-toggle]');
    const waveCurrent = root.querySelector('[data-renao-qr-wave-current]');
    const gradientStops = root.querySelectorAll('[data-renao-qr-gradient-stop]');
    const proofOpen = root.querySelector('[data-renao-qr-proof-open]');
    const proofModal = root.querySelector('[data-renao-qr-proof-modal]');
    const proofStage = root.querySelector('[data-renao-qr-proof-stage]');
    const proofCloseButtons = root.querySelectorAll('[data-renao-qr-proof-close]');
    const previewOriginalParent = preview?.parentElement || null;
    const previewOriginalNext = preview?.nextSibling || null;

    const requiresPhoto = root.dataset.renaoQrRequiresPhoto !== 'false';
    const requiresAudio = root.dataset.renaoQrRequiresAudio === 'true';
    const isSpectrumLayout = root.dataset.renaoQrLayout === 'spectrum';

    if (!preview || !poster || !noteInput) return;
    if (requiresPhoto && (!photoInput || !photoPreview)) return;
    if (requiresAudio && !audioInput) return;
    if (proofModal && proofModal.parentElement !== document.body) document.body.appendChild(proofModal);
    if (cropModal && cropModal.parentElement !== document.body) document.body.appendChild(cropModal);
    if (cropImage) cropImage.draggable = false;

    let photoUrl = '';
    let cropState = { scale: 1, x: 0, y: 0 };
    let cropReferenceSize = { width: 0, height: 0 };
    let dragState = null;

    const getProductForm = () => root.closest('form');

    const getProductMediaWrapper = () => {
      const product = root.closest('.product') || document.querySelector('.product');
      return product ? product.querySelector('.product__media-wrapper') : null;
    };

    const showProductMedia = () => {
      const mediaWrapper = getProductMediaWrapper();
      if (mediaWrapper) {
        mediaWrapper.classList.remove('has-renao-live-preview');
        mediaWrapper.classList.remove('is-renao-media-returning');
        void mediaWrapper.offsetWidth;
        mediaWrapper.classList.add('is-renao-media-returning');
        window.setTimeout(() => mediaWrapper.classList.remove('is-renao-media-returning'), 900);
      }

      root.classList.remove('has-renao-live-preview');
      if (previewOriginalParent && preview.parentElement !== previewOriginalParent) {
        previewOriginalParent.insertBefore(preview, previewOriginalNext);
      }
    };

    const ensureLivePreviewDock = (mediaWrapper) => {
      let dock = mediaWrapper.querySelector('[data-renao-qr-live-preview-dock]');
      if (dock) return dock;

      dock = document.createElement('div');
      dock.className = 'renao-live-preview-dock renao-live-preview-dock--qr';
      dock.dataset.renaoQrLivePreviewDock = '';
      dock.innerHTML = `
        <div class="renao-live-preview-dock__bar">
          <span class="renao-live-preview-dock__title">
            <span class="renao-live-preview-dock__mark" aria-hidden="true"></span>
            <span>
              <strong>Canlı önizleme</strong>
              <small>Baskıdan önceki son tasarım</small>
            </span>
          </span>
          <button type="button" data-renao-qr-show-product-media>Ürün fotoğrafları</button>
        </div>
        <div class="renao-live-preview-dock__slot" data-renao-qr-live-preview-slot></div>
      `;
      mediaWrapper.insertBefore(dock, mediaWrapper.firstChild);
      dock.querySelector('[data-renao-qr-show-product-media]')?.addEventListener('click', showProductMedia);
      return dock;
    };

    const showLivePreview = () => {
      const mediaWrapper = getProductMediaWrapper();
      if (!mediaWrapper) return;

      const dock = ensureLivePreviewDock(mediaWrapper);
      const slot = dock.querySelector('[data-renao-qr-live-preview-slot]');
      if (!slot) return;

      if (preview.parentElement !== slot) slot.appendChild(preview);
      mediaWrapper.classList.remove('is-renao-media-returning');
      mediaWrapper.classList.add('has-renao-live-preview');
      root.classList.add('has-renao-live-preview');
      window.requestAnimationFrame(fitNoteText);
    };

    const clampZoom = (value) => clamp(Number(value || 1), 1, 2.6);

    const getCropReferenceStage = () => {
      if (cropStage && !cropModal?.hidden && cropStage.clientWidth && cropStage.clientHeight) return cropStage;
      const previewStage = photoPreview?.parentElement;
      if (previewStage?.clientWidth && previewStage?.clientHeight) return previewStage;
      return null;
    };

    const clampPhotoPosition = () => {
      const stage = getCropReferenceStage();
      if (stage && cropStage && !cropModal?.hidden) {
        cropReferenceSize = { width: stage.clientWidth, height: stage.clientHeight };
      } else if (!cropReferenceSize.width && stage) {
        cropReferenceSize = { width: stage.clientWidth, height: stage.clientHeight };
      }

      const referenceWidth = cropReferenceSize.width || stage?.clientWidth || 0;
      const referenceHeight = cropReferenceSize.height || stage?.clientHeight || 0;
      if (!referenceWidth || !referenceHeight) return;
      if (!cropState.normalizedPositionApplied
        && Number.isFinite(cropState.normalizedX) && Number.isFinite(cropState.normalizedY)) {
        cropState.x = cropState.normalizedX * referenceWidth;
        cropState.y = cropState.normalizedY * referenceHeight;
        cropState.normalizedPositionApplied = true;
      }
      const maxX = Math.max(0, (referenceWidth * (cropState.scale - 1)) / 2);
      const maxY = Math.max(0, (referenceHeight * (cropState.scale - 1)) / 2);
      cropState.x = Math.min(maxX, Math.max(-maxX, cropState.x));
      cropState.y = Math.min(maxY, Math.max(-maxY, cropState.y));
      cropState.normalizedX = cropState.x / referenceWidth;
      cropState.normalizedY = cropState.y / referenceHeight;
    };

    const getPhotoTransformForStage = (targetStage) => {
      const referenceWidth = cropReferenceSize.width || cropStage?.clientWidth || targetStage?.clientWidth || 1;
      const referenceHeight = cropReferenceSize.height || cropStage?.clientHeight || targetStage?.clientHeight || 1;
      const ratioX = targetStage?.clientWidth ? targetStage.clientWidth / referenceWidth : 1;
      const ratioY = targetStage?.clientHeight ? targetStage.clientHeight / referenceHeight : 1;
      return `translate(${cropState.x * ratioX}px, ${cropState.y * ratioY}px) scale(${cropState.scale})`;
    };

    const syncPhotoPosition = () => {
      clampPhotoPosition();
      if (photoPreview) {
        const previewStage = photoPreview.parentElement || cropStage;
        photoPreview.style.transform = previewStage ? getPhotoTransformForStage(previewStage) : `scale(${cropState.scale})`;
        photoPreview.style.transformOrigin = 'center';
      }
      if (cropImage) {
        const imageStage = cropStage || cropImage.parentElement;
        cropImage.style.transform = imageStage ? getPhotoTransformForStage(imageStage) : `scale(${cropState.scale})`;
        cropImage.style.transformOrigin = 'center';
      }
      if (positionInput) {
        const normalized = Number.isFinite(cropState.normalizedX) && Number.isFinite(cropState.normalizedY)
          ? `, nx:${cropState.normalizedX.toFixed(6)}, ny:${cropState.normalizedY.toFixed(6)}`
          : '';
        const legacy = `x:${Math.round(cropState.x)}, y:${Math.round(cropState.y)}, zoom:${cropState.scale.toFixed(2)}${normalized}`;
        positionInput.value = globalThis.RenaoCropRecipe?.merge
          ? globalThis.RenaoCropRecipe.merge(positionInput.value, legacy, photoPreview, photoPreview?.parentElement)
          : legacy;
      }
    };

    const parsePhotoPosition = (value) => {
      const raw = String(value || '');
      if (positionInput) positionInput.value = raw;
      const xMatch = raw.match(/(?:^|[;,]\s*)x\s*:\s*(-?\d+(?:\.\d+)?)/i);
      const yMatch = raw.match(/(?:^|[;,]\s*)y\s*:\s*(-?\d+(?:\.\d+)?)/i);
      const zoomMatch = raw.match(/zoom\s*:\s*(\d+(?:\.\d+)?)/i);
      const normalizedX = raw.match(/(?:^|[,;]\s*)nx\s*:\s*(-?\d+(?:\.\d+)?)/i);
      const normalizedY = raw.match(/(?:^|[,;]\s*)ny\s*:\s*(-?\d+(?:\.\d+)?)/i);
      const legacyPercent = raw.includes(';') && raw.includes('zoom:') && raw.includes('x:') && raw.includes('y:');
      cropState = {
        x: legacyPercent ? 0 : (xMatch ? Number.parseFloat(xMatch[1]) || 0 : cropState.x),
        y: legacyPercent ? 0 : (yMatch ? Number.parseFloat(yMatch[1]) || 0 : cropState.y),
        scale: zoomMatch ? clampZoom(Number.parseFloat(zoomMatch[1]) || 1) : cropState.scale,
        normalizedX: normalizedX ? Number.parseFloat(normalizedX[1]) : Number.NaN,
        normalizedY: normalizedY ? Number.parseFloat(normalizedY[1]) : Number.NaN,
        normalizedPositionApplied: false,
      };
      if (cropZoom) cropZoom.value = cropState.scale;
      syncPhotoPosition();
    };

    const getDisplayPhotoUrl = () => photoUrl || photoPreview?.getAttribute('src') || '';

    const openCrop = () => {
      const cropSource = getDisplayPhotoUrl();
      if (!cropModal || !cropSource) return;
      cropModal.hidden = false;
      document.documentElement.classList.add('renao-qr-crop-open');
      if (cropImage) cropImage.src = cropSource;
      if (cropZoom) cropZoom.value = cropState.scale;
      syncPhotoPosition();
    };

    const closeCrop = () => {
      if (!cropModal) return;
      cropModal.hidden = true;
      document.documentElement.classList.remove('renao-qr-crop-open');
      dragState = null;
      syncPhotoPosition();
    };

    const setZoom = (nextZoom) => {
      cropState.scale = clampZoom(nextZoom);
      if (cropZoom) cropZoom.value = cropState.scale;
      syncPhotoPosition();
    };

    const moveCrop = (direction, amount = 14) => {
      if (direction === 'up') cropState.y -= amount;
      if (direction === 'down') cropState.y += amount;
      if (direction === 'left') cropState.x -= amount;
      if (direction === 'right') cropState.x += amount;
      syncPhotoPosition();
    };

    const setPhotoSource = (src, caption = '') => {
      if (!src) return;
      photoPreview.src = src;
      poster.classList.add('has-photo');
      root.classList.add('has-photo');
      root.classList.remove('has-renao-qr-photo-error');
      if (photoAdjustToggle) photoAdjustToggle.hidden = false;
      if (photoTitle) photoTitle.textContent = 'Fotoğraf eklendi';
      if (photoCaption) photoCaption.textContent = caption || 'Fotoğraf önizlemeye yerleşti.';
      syncPhotoPosition();
      showLivePreview();
    };

    const syncPhoto = () => {
      const file = photoInput.files && photoInput.files[0];
      if (!file) return;
      if (photoUrl) URL.revokeObjectURL(photoUrl);
      photoUrl = URL.createObjectURL(file);
      root.querySelector('[data-renao-qr-existing-photo]')?.remove();
      cropState = { scale: 1, x: 0, y: 0 };
      cropReferenceSize = { width: 0, height: 0 };
      setPhotoSource(photoUrl, file.name);
      openCrop();
    };

    const syncAudio = () => {
      const file = audioInput?.files && audioInput.files[0];
      if (!file) return;
      root.querySelector('[data-renao-qr-existing-audio]')?.remove();
      root.classList.remove('has-renao-qr-audio-error');
      root.dataset.renaoExistingAudio = 'false';
      if (audioTitle) audioTitle.textContent = 'Ses dosyası eklendi';
      if (audioCaption) audioCaption.textContent = file.name;
    };

    const fitNoteText = () => {
      if (!notePreview) return;
      notePreview.style.fontSize = '';
      const noteStyles = getComputedStyle(notePreview);
      const baseSize = Number.parseFloat(noteStyles.fontSize) || (isSpectrumLayout ? 24 : 32);
      const minSize = Number.parseFloat(noteStyles.getPropertyValue('--rq-note-min-size')) || Math.max(12, Math.round(baseSize * 0.68));
      let size = baseSize;
      while (notePreview.scrollWidth > notePreview.clientWidth && size > minSize) {
        size -= 1;
        notePreview.style.fontSize = `${size}px`;
      }
    };

    const syncNote = () => {
      if (noteInput.maxLength > 0 && noteInput.value.length > noteInput.maxLength) {
        noteInput.value = noteInput.value.slice(0, noteInput.maxLength);
      }
      const value = String(noteInput.value || '').replace(/\s+/g, ' ').trim();
      notePreview.textContent = value || noteInput.placeholder || 'Seni çok seviyorum...';
      if (noteCount) noteCount.textContent = `${noteInput.value.length}/${noteInput.maxLength}`;
      fitNoteText();
      showLivePreview();
    };

    const readCssValue = (name) => getComputedStyle(root).getPropertyValue(name).trim();

    const syncGradientStops = () => {
      const values = ['--rq-wave-a', '--rq-wave-b', '--rq-wave-c', '--rq-wave-d', '--rq-wave-e']
        .map(readCssValue)
        .filter(Boolean);
      gradientStops.forEach((stop, index) => {
        stop.setAttribute('stop-color', values[index] || values[values.length - 1] || '#050505');
      });
    };

    const selectNoteFont = (buttonOrValue) => {
      const button = typeof buttonOrValue === 'string'
        ? Array.from(root.querySelectorAll('[data-renao-qr-font-option]')).find((option) => option.dataset.renaoQrFontValue === buttonOrValue || option.dataset.renaoQrFontLabel === buttonOrValue)
        : buttonOrValue;
      if (!button) return;

      const value = button.dataset.renaoQrFontValue || 'script';
      const label = button.dataset.renaoQrFontLabel || 'El Yazısı';
      poster.dataset.renaoQrNoteFont = value;
      if (fontProperty) fontProperty.value = label;
      root.querySelectorAll('[data-renao-qr-font-option]').forEach((option) => {
        const isSelected = option === button;
        option.classList.toggle('is-selected', isSelected);
        option.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      });
      window.requestAnimationFrame(fitNoteText);
      showLivePreview();
    };

    const selectQrCode = (buttonOrValue) => {
      const button = typeof buttonOrValue === 'string'
        ? Array.from(root.querySelectorAll('[data-renao-qr-code-option]')).find((option) => option.dataset.renaoQrCodeLabel === buttonOrValue || option.dataset.renaoQrCodeValue === buttonOrValue)
        : buttonOrValue;
      if (!button) return;

      const value = button.dataset.renaoQrCodeValue === 'true';
      const label = button.dataset.renaoQrCodeLabel || (value ? 'Evet' : 'Hayır');
      poster.dataset.renaoQrShowCode = value ? 'true' : 'false';
      if (codeProperty) codeProperty.value = label;
      root.querySelectorAll('[data-renao-qr-code-option]').forEach((option) => {
        const isSelected = option === button;
        option.classList.toggle('is-selected', isSelected);
        option.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      });
      showLivePreview();
    };

    const selectWave = (buttonOrLabel) => {
      const button = typeof buttonOrLabel === 'string'
        ? Array.from(root.querySelectorAll('[data-renao-qr-wave-option]')).find((option) => option.dataset.renaoQrWaveLabel === buttonOrLabel)
        : buttonOrLabel;
      if (!button) return;

      const label = button.dataset.renaoQrWaveLabel || 'Tasarım 4';
      const mode = button.dataset.renaoQrWaveMode || 'spectrum';
      const palette = button.dataset.renaoQrWavePalette || 'muted';
      const imageUrl = button.dataset.renaoQrWaveImage || '';
      root.dataset.renaoQrPalette = palette;
      poster.dataset.renaoQrPalette = palette;
      poster.dataset.renaoQrWaveMode = mode;
      poster.classList.toggle('is-custom-wave', Boolean(imageUrl));
      if (wavePdf) {
        if (imageUrl) {
          wavePdf.src = imageUrl;
          wavePdf.hidden = false;
        } else {
          wavePdf.removeAttribute('src');
          wavePdf.hidden = true;
        }
      }
      if (waveProperty) waveProperty.value = label;
      if (waveCurrent) waveCurrent.textContent = label;
      root.querySelectorAll('[data-renao-qr-wave-option]').forEach((option) => {
        const isSelected = option === button;
        option.classList.toggle('is-selected', isSelected);
        option.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      });
      if (waveField && window.matchMedia('(max-width: 749px)').matches) {
        waveField.classList.remove('is-open');
        waveToggle?.setAttribute('aria-expanded', 'false');
      }
      window.requestAnimationFrame(syncGradientStops);
      showLivePreview();
    };

    const openProofModal = () => {
      if (!proofModal || !proofStage) return;
      showLivePreview();
      fitNoteText();
      proofStage.innerHTML = '';
      const clone = poster.cloneNode(true);
      clone.style.maxWidth = '100%';
      proofStage.appendChild(clone);
      proofModal.hidden = false;
      document.documentElement.classList.add('renao-proof-modal-open');
    };

    const closeProofModal = () => {
      if (!proofModal) return;
      proofModal.hidden = true;
      document.documentElement.classList.remove('renao-proof-modal-open');
    };

    const setEditSubmitText = (attempt = 0) => {
      const submitText = getProductForm()?.querySelector('[type="submit"] span');
      if (submitText) {
        submitText.textContent = 'Düzenlemeyi Bitir';
        return;
      }
      if (attempt < 12) window.setTimeout(() => setEditSubmitText(attempt + 1), 80);
    };

    const restoreEditState = () => {
      const params = new URLSearchParams(window.location.search);
      const editKey = params.get('renao_edit');
      if (!editKey) return;

      let editState = null;
      try {
        editState = JSON.parse(window.sessionStorage.getItem(`renaoCartEdit:${editKey}`) || 'null');
      } catch (error) {
        console.warn('Renao QR cart edit state could not be restored.', error);
      }
      if (!editState) return;

      const properties = normalizeProperties(editState.properties);
      const productForm = getProductForm();
      if (productForm) {
        productForm.dataset.renaoEditKey = editState.key || editKey;
        productForm.dataset.renaoEditStorageKey = `renaoCartEdit:${editState.key || editKey}`;
        const quantityInput = productForm.querySelector('input[name="quantity"]');
        if (quantityInput && editState.quantity) quantityInput.value = editState.quantity;
        setEditSubmitText();
      }

      if (Object.prototype.hasOwnProperty.call(properties, 'Not')) {
        noteInput.value = String(properties.Not || '').slice(0, noteInput.maxLength || 140);
      }
      if (properties['Alt Not Fontu']) selectNoteFont(properties['Alt Not Fontu']);
      if (properties['QR Kod']) selectQrCode(properties['QR Kod']);
      if (orderNoteInput && properties['Sipariş Notu']) orderNoteInput.value = properties['Sipariş Notu'];
      if (properties['Premium Ses Dalgası']) selectWave(properties['Premium Ses Dalgası']);
      if (properties['_Fotoğraf Konumu']) parsePhotoPosition(properties['_Fotoğraf Konumu']);

      const existingPhoto = properties['_Fotoğraf'] || properties['Fotoğraf'];
      if (existingPhoto && photoInput) {
        const hiddenInput = document.createElement('input');
        hiddenInput.type = 'hidden';
        hiddenInput.name = 'properties[Fotoğraf]';
        hiddenInput.value = existingPhoto;
        hiddenInput.dataset.renaoQrExistingPhoto = 'true';
        photoInput.insertAdjacentElement('afterend', hiddenInput);
        root.dataset.renaoExistingPhoto = 'true';
        setPhotoSource(existingPhoto, existingPhoto.split('/').pop() || 'Mevcut fotoğraf korunacak.');
      }

      const existingAudio = properties['Ses Dosyası'];
      if (existingAudio && audioInput) {
        const hiddenInput = document.createElement('input');
        hiddenInput.type = 'hidden';
        hiddenInput.name = 'properties[Ses Dosyası]';
        hiddenInput.value = existingAudio;
        hiddenInput.dataset.renaoQrExistingAudio = 'true';
        audioInput.insertAdjacentElement('afterend', hiddenInput);
        root.dataset.renaoExistingAudio = 'true';
        root.classList.remove('has-renao-qr-audio-error');
        if (audioTitle) audioTitle.textContent = 'Ses dosyası eklendi';
        if (audioCaption) audioCaption.textContent = existingAudio.split('/').pop() || 'Mevcut ses dosyası korunacak.';
      }

      const giftWrapSelect = productForm?.querySelector('[data-renao-gift-wrap-select]');
      if (giftWrapSelect && properties['Hediye Paketi']) giftWrapSelect.value = properties['Hediye Paketi'];
      syncNote();
    };

    photoButton?.addEventListener('click', () => photoInput?.click());
    audioButton?.addEventListener('click', () => audioInput?.click());
    photoAdjustToggle?.addEventListener('click', openCrop);
    photoInput?.addEventListener('change', syncPhoto);
    audioInput?.addEventListener('change', syncAudio);
    waveToggle?.addEventListener('click', () => {
      const isOpen = !waveField?.classList.contains('is-open');
      waveField?.classList.toggle('is-open', isOpen);
      waveToggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });
    cropStage?.addEventListener('pointerdown', (event) => {
      if (!getDisplayPhotoUrl()) return;
      event.preventDefault();
      event.stopPropagation();
      cropStage.setPointerCapture(event.pointerId);
      dragState = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originX: cropState.x,
        originY: cropState.y,
      };
    });
    cropStage?.addEventListener('pointermove', (event) => {
      if (!dragState || dragState.pointerId !== event.pointerId) return;
      event.preventDefault();
      event.stopPropagation();
      cropState.x = dragState.originX + event.clientX - dragState.startX;
      cropState.y = dragState.originY + event.clientY - dragState.startY;
      syncPhotoPosition();
    });
    cropStage?.addEventListener('pointerup', (event) => {
      event.preventDefault();
      if (dragState?.pointerId === event.pointerId) dragState = null;
    });
    cropStage?.addEventListener('dragstart', (event) => event.preventDefault());
    cropStage?.addEventListener('pointercancel', () => {
      dragState = null;
    });
    cropStage?.addEventListener('lostpointercapture', () => {
      dragState = null;
    });
    cropStage?.addEventListener('wheel', (event) => {
      if (!getDisplayPhotoUrl()) return;
      event.preventDefault();
      const step = event.deltaY > 0 ? -0.08 : 0.08;
      setZoom(cropState.scale + step);
    }, { passive: false });
    cropZoom?.addEventListener('input', () => setZoom(cropZoom.value));
    cropMoveButtons.forEach((button) => {
      button.addEventListener('click', () => moveCrop(button.dataset.renaoQrCropMove));
    });
    cropZoomStepButtons.forEach((button) => {
      button.addEventListener('click', () => setZoom(cropState.scale + Number(button.dataset.renaoQrCropZoomStep || 0)));
    });
    cropReset?.addEventListener('click', () => {
      cropState = { scale: 1, x: 0, y: 0 };
      cropReferenceSize = { width: 0, height: 0 };
      setZoom(1);
    });
    cropApply?.addEventListener('click', closeCrop);
    cropCancelButtons.forEach((button) => button.addEventListener('click', closeCrop));
    noteInput.addEventListener('input', syncNote);
    root.addEventListener('click', (event) => {
      const waveButton = event.target.closest('[data-renao-qr-wave-option]');
      if (waveButton) selectWave(waveButton);
      const fontButton = event.target.closest('[data-renao-qr-font-option]');
      if (fontButton) selectNoteFont(fontButton);
      const codeButton = event.target.closest('[data-renao-qr-code-option]');
      if (codeButton) selectQrCode(codeButton);
    });
    root.addEventListener('focusin', showLivePreview);
    proofOpen?.addEventListener('click', openProofModal);
    proofCloseButtons.forEach((button) => button.addEventListener('click', closeProofModal));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && proofModal && !proofModal.hidden) closeProofModal();
      if (event.key === 'Escape' && cropModal && !cropModal.hidden) closeCrop();
    });
    window.addEventListener('resize', fitNoteText);

    restoreEditState();
    syncPhotoPosition();
    syncNote();
    selectNoteFont(root.querySelector('[data-renao-qr-font-option].is-selected'));
    selectQrCode(root.querySelector('[data-renao-qr-code-option].is-selected'));
    selectWave(root.querySelector('[data-renao-qr-wave-option].is-selected'));
  };

  const boot = () => {
    document.querySelectorAll('[data-renao-qr-personalizer]').forEach(initQrPersonalizer);
  };

  document.addEventListener('renao:qr:validate', (event) => {
    const root = event.target.closest('[data-renao-qr-personalizer]');
    if (!root) return;
    let valid = true;
    const ensureCanonicalNote = () => {
      const noteInput = root.querySelector('[data-renao-qr-note-input]');
      if (!noteInput) return;
      const fallback = String(noteInput.getAttribute('placeholder') || '').trim();
      const maxLength = Number.parseInt(noteInput.getAttribute('maxlength') || '', 10);
      const canonical = String(noteInput.value || '').replace(/\s+/g, ' ').trim() || fallback;
      noteInput.value = Number.isFinite(maxLength) && maxLength > 0 ? canonical.slice(0, maxLength) : canonical;
      noteInput.dispatchEvent(new Event('input', { bubbles: true }));
    };

    if (root.dataset.renaoQrRequiresAudio === 'true') {
      const audioInput = root.querySelector('[data-renao-qr-audio-input]');
      const existingAudioInput = root.querySelector('[data-renao-qr-existing-audio]');
      const hasNewAudio = Boolean(audioInput?.files && audioInput.files.length > 0);
      const hasExistingAudio = Boolean(existingAudioInput?.value || root.dataset.renaoExistingAudio === 'true');
      const audioValid = hasNewAudio || hasExistingAudio;
      root.classList.toggle('has-renao-qr-audio-error', !audioValid);
      if (!audioValid) {
        event.detail.message = 'Ses dosyası yüklemeden sepete ekleyemezsin.';
        event.detail.focusSelector = '[data-renao-qr-audio-button]';
      }
      valid = valid && audioValid;
    }

    if (root.dataset.renaoQrRequiresPhoto === 'false') {
      if (valid) ensureCanonicalNote();
      event.detail.valid = valid;
      root.classList.remove('has-renao-qr-photo-error');
      return;
    }

    const photoInput = root.querySelector('[data-renao-qr-photo-input]');
    const existingPhotoInput = root.querySelector('[data-renao-qr-existing-photo]');
    const hasNewPhoto = Boolean(photoInput?.files && photoInput.files.length > 0);
    const hasExistingPhoto = Boolean(existingPhotoInput?.value || root.dataset.renaoExistingPhoto === 'true');
    const photoValid = hasNewPhoto || hasExistingPhoto;
    root.classList.toggle('has-renao-qr-photo-error', !photoValid);
    if (!photoValid && !event.detail.message) {
      event.detail.message = 'Fotoğraf seçmeden sepete ekleyemezsin.';
      event.detail.focusSelector = '[data-renao-qr-photo-button]';
    }
    valid = valid && photoValid;
    if (valid) ensureCanonicalNote();
    event.detail.valid = valid;
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();

/* Preserve QR-plaque customer photos exactly as uploaded. The existing crop
 * recipe continues to store the buyer's x/y/zoom and source-pixel crop. */
(() => {
  const endpoint = 'https://renao-customer-photos.renaogift.workers.dev/api/customer-photos';
  const boot = (root) => {
    if (root.dataset.renaoQrOriginalUploadReady === 'true') return;
    root.dataset.renaoQrOriginalUploadReady = 'true';
    root.dataset.renaoFileManaged = 'true';
    const input = root.querySelector('[data-renao-qr-photo-input]');
    const state = { file: null, url: '', promise: null, error: null };
    const upload = () => {
      const file = state.file || input?.files?.[0] || null;
      if (!(file instanceof File) || !file.size) return Promise.resolve(state.url);
      if (state.url) return Promise.resolve(state.url);
      if (state.promise) return state.promise;
      const url = new URL(endpoint);
      url.searchParams.set('token', crypto.randomUUID());
      url.searchParams.set('slot', 'qr-photo');
      url.searchParams.set('fileName', file.name || 'foto');
      state.promise = fetch(url, { method: 'POST', headers: { 'Content-Type': file.type || 'application/octet-stream' }, body: file, credentials: 'omit' })
        .then(async (response) => { if (!response.ok) throw new Error(await response.text() || 'Fotoğraf yüklenemedi.'); return response.json(); })
        .then((result) => { if (!result?.url) throw new Error('Orijinal fotoğraf bağlantısı oluşturulamadı.'); state.url = String(result.url); return state.url; })
        .catch((error) => { state.promise = null; state.error = error; throw error; });
      return state.promise;
    };
    input?.addEventListener('change', () => {
      state.file = input.files?.[0] || null; state.url = ''; state.promise = null; state.error = null;
      upload().catch((error) => console.warn('Renao QR original photo upload will retry at cart submission.', error));
    });
    root.renaoPrepareManagedFiles = async () => { await upload(); return true; };
    root.renaoAppendManagedFiles = (formData) => {
      if (!formData || !state.url || !input?.name) return false;
      formData.delete(input.name); formData.append(input.name, state.url); return true;
    };
  };
  const bootAll = () => document.querySelectorAll('[data-renao-qr-personalizer]').forEach(boot);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bootAll); else bootAll();
  document.addEventListener('shopify:section:load', bootAll);
})();
